/**
 * Session state (§6.1).
 *
 * Sanctum's cookie is the actual credential — this store only mirrors who that cookie
 * belongs to, so `user` is always re-derived from `GET /v1/me` rather than persisted.
 * Persisting it would let a stale localStorage entry render an authenticated shell for a
 * session the server has already ended.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, ensureCsrfCookie, errorMessage, resetCsrf } from '@/api/client'

export interface Workspace {
  id: string
  name: string
  slug: string
  role?: string
  enforcement: string
  onboarded: boolean
  requires_2fa: boolean
  plan?: { id: string; name: string; limits: Record<string, unknown> }
  default_region?: { id: string; name: string } | null
}

export interface AuthUser {
  id: string
  name: string
  email: string
  avatar_url: string | null
  theme: 'system' | 'dark' | 'light'
  email_verified: boolean
  two_factor_enabled: boolean
  workspaces?: Workspace[]
  oauth_providers?: string[]
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const loading = ref(false)

  /** Distinguishes "not signed in" from "haven't checked yet" — the router guard needs both. */
  const resolved = ref(false)

  /** The short-lived token from a password step awaiting its TOTP code. */
  const twoFactorChallenge = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const needsEmailVerification = computed(() => user.value !== null && !user.value.email_verified)

  async function fetchUser(): Promise<AuthUser | null> {
    loading.value = true
    try {
      const { data, error } = await api.GET('/v1/me')

      // A 401 here is the normal "no session" case, not an exception.
      user.value = error ? null : ((data as { data: AuthUser }).data ?? null)
      return user.value
    } finally {
      loading.value = false
      resolved.value = true
    }
  }

  async function login(email: string, password: string, remember = false) {
    await ensureCsrfCookie()

    const { data, error } = await api.POST('/v1/auth/login', {
      body: { email, password, remember },
    })

    if (error) throw error

    const payload = (data as { data: Record<string, unknown> }).data

    if (payload.requires_2fa === true) {
      twoFactorChallenge.value = payload.challenge_token as string
      return { requiresTwoFactor: true as const }
    }

    user.value = payload as unknown as AuthUser
    twoFactorChallenge.value = null
    return { requiresTwoFactor: false as const }
  }

  async function verifyTwoFactor(input: { code?: string; recoveryCode?: string }) {
    if (twoFactorChallenge.value === null) {
      throw new Error('No two-factor challenge in progress. Sign in again.')
    }

    const { data, error } = await api.POST('/v1/auth/2fa/verify', {
      body: {
        challenge_token: twoFactorChallenge.value,
        code: input.code,
        recovery_code: input.recoveryCode,
      },
    })

    if (error) throw error

    user.value = (data as { data: AuthUser }).data
    twoFactorChallenge.value = null
  }

  async function register(input: {
    name: string
    email: string
    password: string
    passwordConfirmation: string
    workspaceName?: string
  }) {
    await ensureCsrfCookie()

    const { data, error } = await api.POST('/v1/auth/register', {
      body: {
        name: input.name,
        email: input.email,
        password: input.password,
        password_confirmation: input.passwordConfirmation,
        workspace_name: input.workspaceName,
      },
    })

    if (error) throw error

    user.value = (data as { data: AuthUser }).data
  }

  async function logout() {
    try {
      await api.POST('/v1/auth/logout')
    } catch {
      // Clear locally regardless: a failed logout call must not leave the UI signed in.
    } finally {
      user.value = null
      twoFactorChallenge.value = null
      resetCsrf()
    }
  }

  return {
    user,
    loading,
    resolved,
    twoFactorChallenge,
    isAuthenticated,
    needsEmailVerification,
    fetchUser,
    login,
    verifyTwoFactor,
    register,
    logout,
    errorMessage,
  }
})
